import { useState } from "react";
import { Link } from "wouter";
import { ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import LegalFooter from "@/components/legal-footer";
import { useToast } from "@/hooks/use-toast";
import { Mail } from "lucide-react";

const SUPPORT_EMAIL = "support@onenext.app";

const CATEGORIES = [
  { value: "general", label: "Question générale" },
  { value: "bug", label: "Signaler un bug" },
  { value: "billing", label: "Facturation" },
  { value: "rgpd", label: "Données personnelles / RGPD" },
  { value: "feature", label: "Suggestion d'amélioration" },
] as const;

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function ContactPage() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: "",
    name: "",
    subject: "",
    message: "",
    category: "general",
    consent: false,
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${basePath}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(data.error ?? "Échec de l'envoi");
      }
      setSubmitted(true);
    } catch (err) {
      toast({
        title: "Erreur",
        description:
          err instanceof Error
            ? err.message
            : "Impossible d'envoyer votre demande pour le moment.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col">
      <header className="border-b">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" /> Accueil
            </Button>
          </Link>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <div className="w-6 h-6 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs">
              O
            </div>
            OneNext
          </div>
        </div>
      </header>
      <main className="flex-1 mx-auto w-full max-w-2xl px-6 py-10">
        <p className="text-xs uppercase tracking-widest text-primary font-semibold">
          Support
        </p>
        <h1 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight">
          Nous contacter
        </h1>
        <p className="mt-3 text-muted-foreground">
          Une question, un bug, une demande RGPD ? Écrivez-nous, nous
          répondons sous 48h ouvrées.
        </p>

        <div
          className="mt-5 rounded-xl border bg-card p-4 flex items-start gap-3"
          data-testid="contact-support-email"
        >
          <div className="shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="w-4 h-4 text-primary" />
          </div>
          <div className="text-sm">
            <p className="font-medium">Adresse e-mail support</p>
            <p className="text-muted-foreground">
              Vous pouvez aussi nous écrire directement à{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-primary hover:underline font-medium"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </div>
        </div>

        {submitted ? (
          <div
            className="mt-8 rounded-2xl border bg-card p-6 text-center"
            data-testid="contact-success"
          >
            <CheckCircle2 className="w-10 h-10 text-primary mx-auto" />
            <h2 className="mt-4 text-xl font-semibold">Message envoyé</h2>
            <p className="mt-2 text-muted-foreground">
              Merci, nous avons bien reçu votre demande. Vous recevrez une
              réponse à l'adresse indiquée.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Link href="/">
                <Button variant="outline">Retour à l'accueil</Button>
              </Link>
              <Button onClick={() => { setSubmitted(false); setForm({ email: "", name: "", subject: "", message: "", category: "general", consent: false }); }}>
                Envoyer une autre demande
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-8 space-y-5"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact-email">
                  Adresse e-mail <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-name">Nom (facultatif)</Label>
                <Input
                  id="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm({ ...form, category: v })}
              >
                <SelectTrigger data-testid="contact-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-subject">
                Sujet <span className="text-destructive">*</span>
              </Label>
              <Input
                id="contact-subject"
                required
                minLength={3}
                maxLength={200}
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                data-testid="contact-subject"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-message">
                Message <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="contact-message"
                required
                minLength={10}
                maxLength={5000}
                rows={7}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
              />
              <p className="text-xs text-muted-foreground">
                {form.message.length} / 5000 caractères
              </p>
            </div>

            <div className="flex items-start gap-3 rounded-xl border bg-card/50 p-3">
              <Checkbox
                id="contact-consent"
                checked={form.consent}
                onCheckedChange={(v) =>
                  setForm({ ...form, consent: v === true })
                }
                data-testid="contact-consent"
              />
              <Label
                htmlFor="contact-consent"
                className="text-sm font-normal text-muted-foreground cursor-pointer leading-relaxed"
              >
                J'accepte que les informations transmises soient utilisées
                pour traiter ma demande, conformément à la{" "}
                <Link
                  href="/legal/confidentialite"
                  className="text-primary hover:underline"
                >
                  politique de confidentialité
                </Link>
                .
              </Label>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full sm:w-auto gap-2"
              disabled={submitting || !form.consent}
              data-testid="contact-submit"
            >
              <Send className="w-4 h-4" />
              {submitting ? "Envoi en cours…" : "Envoyer ma demande"}
            </Button>
          </form>
        )}
      </main>
      <LegalFooter />
    </div>
  );
}
